import { MissionAssessmentRequestCreateDto, TechnologySelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ExpertMatchingStep } from '@components/pages/technology/components/technology-mission-assessment-request-expert-matching-step.component';
import { TechnologyParametersStep } from '@components/pages/technology/components/technology-mission-assessment-request-technology-parameters-step.component';
import { ModalSteps, StepConfig, useSteps } from '@components/shared/modal-steps.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast } from '@hooks/toast.hook';
import { useCreateMissionAssessmentRequest } from '@queries/mission-assessment-requests.query';
import {
  type MissionAssessmentRequestFormData,
  expertMatchingStepSchema,
  missionAssessmentRequestSchema,
  technologyParametersStepSchema,
} from '@schemas/mission-assessment-request.schema';
import { useStore } from '@tanstack/react-form';
import { createFormInvalidHandler } from '@utils/form.util';
import React, { useCallback, useEffect, useMemo } from 'react';

type TechnologyMissionAssessmentRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  technology: TechnologySelectDto;
};

enum Steps {
  TECHNOLOGY_PARAMETERS = 0,
  EXPERT_MATCHING = 1,
}

const stepData: StepConfig[] = [
  {
    label: 'Technology Parameters',
    step: Steps.TECHNOLOGY_PARAMETERS,
    guidance: 'Select the use and domain for this assessment.',
  },
  {
    label: 'Expert Matching',
    step: Steps.EXPERT_MATCHING,
    guidance: 'Specify optional criteria for expert matching.',
  },
];

const stepSchemaMap = {
  [Steps.TECHNOLOGY_PARAMETERS]: technologyParametersStepSchema,
  [Steps.EXPERT_MATCHING]: expertMatchingStepSchema,
};

export const TechnologyMissionAssessmentRequestModal: React.FC<TechnologyMissionAssessmentRequestModalProps> = (
  props,
) => {
  const { isOpen, onClose, technology } = props;

  const { mutateAsync: createMissionAssessmentRequest, isPending } = useCreateMissionAssessmentRequest();

  const defaultValues = useMemo<MissionAssessmentRequestFormData>(
    () => ({
      domainId: '',
      isStrictMatch: false,
      message: '',
      technologyId: technology.id,
      useId: '',
      veteranCountriesOfService: [],
      veteranServiceIds: [],
    }),
    [technology.id],
  );

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: ({ formApi }) => {
        const currentStepSchema = stepSchemaMap[step];
        const errors = formApi.parseValuesWithSchema(currentStepSchema);
        if (errors) return errors;
      },
    },
    canSubmitWhenInvalid: true,
    onSubmitInvalid: createFormInvalidHandler(),
    onSubmit: async ({ value }) => {
      if (step < Steps.EXPERT_MATCHING) {
        updateStepState(step);
        setStep(step + 1);
        return;
      }

      const validationResult = missionAssessmentRequestSchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please fill in all required fields correctly.' });
        return;
      }

      const trimmedMessage = validationResult.data.message?.trim();

      const payload: MissionAssessmentRequestCreateDto = {
        ...validationResult.data,
        message: trimmedMessage || undefined,
        veteranCountriesOfService:
          validationResult.data.veteranCountriesOfService && validationResult.data.veteranCountriesOfService.length > 0
            ? validationResult.data.veteranCountriesOfService
            : undefined,
        veteranServiceIds:
          validationResult.data.veteranServiceIds && validationResult.data.veteranServiceIds.length > 0
            ? validationResult.data.veteranServiceIds
            : undefined,
      };

      await createMissionAssessmentRequest(payload, {
        onSuccess: () => {
          onCloseOverride();
        },
      });
    },
  });

  const {
    step,
    setStep,
    stepsInfo,
    updateStepState,
    handleStepClick: handleStepClickBase,
    resetSteps,
  } = useSteps(stepData, Steps.TECHNOLOGY_PARAMETERS, false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const isWithinCommandOrPopover = (e.target as HTMLElement)?.closest('[role="combobox"]');

    if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement || isWithinCommandOrPopover)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const resetForm = () => {
    form.reset();
    resetSteps();
  };

  const onCloseOverride = () => {
    resetForm();
    onClose();
  };

  const formErrors = useStore(form.store, (state) => state.errors);

  const handleBack = () => {
    if (formErrors.length > 0) {
      errorToast({ description: 'Please fill in all required fields correctly.' });
      return;
    }

    setStep((prevStep) => prevStep - 1);
  };

  const handleStepClick = (targetStep: Steps) => {
    if (formErrors.length > 0) {
      errorToast({ description: 'Please fill in all required fields correctly.' });
      return;
    }

    handleStepClickBase(targetStep);
  };

  useEffect(() => {
    form.setFieldValue('technologyId', technology.id);
  }, [technology.id, form.setFieldValue]);

  const currentStepData = stepData.find((data) => data.step === step);

  return (
    <Modal
      footer={{
        rightButton: {
          loading: isPending,
          onClick: form.handleSubmit,
          children: step === Steps.EXPERT_MATCHING ? 'Request Assessment' : 'Continue',
        },
        leftButton:
          step !== Steps.TECHNOLOGY_PARAMETERS
            ? {
                onClick: handleBack,
                children: 'Back',
                variant: 'secondary',
              }
            : undefined,
      }}
      header={{ title: 'Request Mission Assessment' }}
      isOpen={isOpen}
      onClose={onCloseOverride}
      width="620px"
    >
      <div className="flex flex-col gap-4 py-4">
        <ModalSteps
          stepsInfo={stepsInfo}
          onStepClick={handleStepClick}
        />

        <div className="flex flex-col gap-2">
          <form.AppForm>
            <div className="h-full w-full">
              {step === Steps.TECHNOLOGY_PARAMETERS && (
                <TechnologyParametersStep
                  form={form}
                  technology={technology}
                />
              )}
              {step === Steps.EXPERT_MATCHING && <ExpertMatchingStep form={form} />}
            </div>
          </form.AppForm>
        </div>
      </div>
    </Modal>
  );
};
